/**
 * rateLimit.ts — Client-seitiger Wrapper für check_rate_limit() RPC
 *
 * Ruft die PostgreSQL-Function `check_rate_limit()` auf und liefert ein
 * einfaches Ergebnis zurück, das direkt vor kritischen Aktionen geprüft
 * werden kann.
 *
 * ──────────────────────────────────────────────────────────────────────
 * Nutzung:
 *
 *   import { checkRateLimit, RATE_LIMITS } from '../lib/rateLimit';
 *
 *   const rl = await checkRateLimit(RATE_LIMITS.BID, userId);
 *   if (!rl.ok) {
 *     openToast(t('hcRateLimitExceeded'), 'error');
 *     return;
 *   }
 *   // ... eigentliche Aktion
 *
 * ──────────────────────────────────────────────────────────────────────
 * SECURITY HARDENING (Phase 2, Audit-Befund #12):
 *
 * FAIL-CLOSED statt FAIL-OPEN.
 *
 * Wenn der RPC-Call selbst fehlschlägt (Netzwerkproblem, Supabase down,
 * fehlende Konfiguration), wird die Aktion BLOCKIERT (`ok: false`). Die
 * alte Version ließ bei Fehlern durch (`ok: true`) — ein Angreifer konnte
 * durch absichtliche Netzwerk-Störungen unbegrenzte Versuche erzwingen.
 *
 * Auswirkung auf User-Experience:
 * Im unwahrscheinlichen Fall eines DB-Ausfalls kann ein User kurzzeitig
 * keine Angebote abgeben, keine Aufträge erstellen und keine Chat-
 * Nachrichten senden, bis die DB wieder erreichbar ist. Das ist
 * akzeptabel — die Alternative (unbegrenzter Missbrauch bei DB-Problemen)
 * ist schlimmer.
 *
 * HINWEIS: Dieser Client-seitige Check ist ZUSÄTZLICH zur server-
 * seitigen Absicherung (DB-Trigger in Migration 044). Ein Angreifer,
 * der DevTools öffnet, wird vom Client-Check nicht aufgehalten, aber
 * vom DB-Trigger schon. Der Client-Check existiert ausschließlich für
 * eine gute User-Experience (sofortiges Feedback, keine Wartezeit).
 * ──────────────────────────────────────────────────────────────────────
 */

import { _sb } from './supabase/client.js';

/**
 * Konfiguration eines Rate-Limits.
 */
export interface RateLimitConfig {
  /** Maximale Anzahl erlaubter Calls im Fenster. */
  max: number;
  /** Zeitfenster in Minuten. */
  windowMin: number;
  /** Namespace für den Key (verhindert Kollisionen zwischen Aktions-Typen). */
  keyPrefix: string;
}

/**
 * Ergebnis eines Rate-Limit-Checks.
 *
 * `reason` ist eine geschlossene Union — jeder Fehler-Pfad hat genau
 * einen definierten String, sodass Caller via `if (rl.reason === '...')`
 * compile-time-geprüft auf einzelne Fälle reagieren können.
 */
export interface RateLimitResult {
  /** `true` wenn die Aktion erlaubt ist (Counter wurde inkrementiert). */
  ok: boolean;
  /** Grund bei `ok === false`. Bei `ok === true` undefined. */
  reason?:
    | 'invalid_limit_config'
    | 'missing_identifier'
    | 'no_client'
    | 'rpc_error'
    | 'rate_limit_exceeded'
    | 'network_error';
}

/**
 * Zentrale Definition aller Rate-Limits.
 *
 * Das `as const satisfies Record<...>` Pattern stellt sicher, dass:
 *   1. Jeder Eintrag das `RateLimitConfig`-Schema erfüllt (Compile-Check)
 *   2. Die literale Typ-Information erhalten bleibt (`RATE_LIMITS.BID.max`
 *      ist nicht nur `number`, sondern exakt der Literal-Typ `30`)
 *   3. Die Tabelle readonly ist — versehentliches Überschreiben einzelner
 *      Felder zur Laufzeit wird vom Compiler verhindert
 */
export const RATE_LIMITS = {
  LOGIN:          { max: 10, windowMin: 15, keyPrefix: 'login'   }, // pro E-Mail
  SIGNUP:         { max: 5,  windowMin: 60, keyPrefix: 'signup'  }, // pro User/IP-Proxy
  PW_RESET:       { max: 3,  windowMin: 60, keyPrefix: 'pwreset' }, // pro E-Mail
  BID:            { max: 30, windowMin: 1,  keyPrefix: 'bid'     }, // pro Fahrer
  CREATE_AUFTRAG: { max: 20, windowMin: 1,  keyPrefix: 'crauf'   }, // pro Firma
  CHAT_MSG:       { max: 60, windowMin: 1,  keyPrefix: 'chat'    }, // pro User
  SEND_EMAIL:     { max: 20, windowMin: 60, keyPrefix: 'mail'    }, // pro User
} as const satisfies Record<string, RateLimitConfig>;

/**
 * Prüft Rate-Limit für eine Aktion und einen Identifikator.
 *
 * @param limit      - Eines der `RATE_LIMITS`-Objekte
 * @param identifier - z.B. user-UUID oder E-Mail
 * @returns
 *   - `{ok: true}`                           — Aktion erlaubt, Counter erhöht
 *   - `{ok: false, reason: '...'}`           — Limit überschritten ODER Fehler (fail-closed)
 */
export async function checkRateLimit(
  limit: RateLimitConfig,
  identifier: string,
): Promise<RateLimitResult> {
  // Defensive checks — auch für untypisierte JS-Caller, die hier noch
  // null/undefined einreichen könnten
  if (!limit || !limit.keyPrefix || !limit.max || !limit.windowMin) {
    console.error('[rateLimit] Ungültiges Limit-Objekt:', limit);
    return { ok: false, reason: 'invalid_limit_config' }; // FAIL-CLOSED
  }
  if (!identifier) {
    console.error('[rateLimit] identifier fehlt für', limit.keyPrefix);
    return { ok: false, reason: 'missing_identifier' }; // FAIL-CLOSED
  }
  if (!_sb) {
    // Supabase-Client nicht initialisiert (.env fehlt zur Build-Zeit).
    // Vorher: TypeError-Crash. Jetzt: fail-closed wie bei allen anderen
    // Fehler-Pfaden — konsistent mit der Sicherheits-Doku oben.
    console.error('[rateLimit] Supabase-Client nicht initialisiert');
    return { ok: false, reason: 'no_client' }; // FAIL-CLOSED
  }

  const key = limit.keyPrefix + ':' + String(identifier).toLowerCase().trim();

  try {
    const r = await _sb.rpc('check_rate_limit', {
      p_key: key,
      p_max: limit.max,
      p_window_min: limit.windowMin,
    });

    if (r.error) {
      console.error('[rateLimit] RPC error:', r.error.message);
      return { ok: false, reason: 'rpc_error' }; // FAIL-CLOSED
    }

    // RPC gibt true/false direkt zurück
    if (r.data === false) {
      return { ok: false, reason: 'rate_limit_exceeded' };
    }
    return { ok: true };
  } catch (err: unknown) {
    console.error('[rateLimit] unexpected error:', err);
    return { ok: false, reason: 'network_error' }; // FAIL-CLOSED
  }
}
